import { z } from 'zod'
import { utcTimestampSchema } from './common'

/**
 * Model contract of the model-registry capability, as specified by the backend change
 * `add-model-registry-list-api` (not merged yet; to be checked against `/openapi.json` once it is):
 *   GET /models?q&modelType&ids&sort&order&page&pageSize → ModelList
 *   GET /models/summary?q&modelType                      → ModelSummary
 * `modelType` and `ids` are repeated parameters (`?ids=a&ids=b`, 1 to 100 ids); `ids` returns only
 * those models, which polling reads again. 422 for an unknown sort column, model type or a
 * malformed id. One model is one training job. Needs the access token. Field names are camelCase.
 * Deletion (backend change `delete-models`):
 *   POST /models/delete { ids }   → 204, every listed model is deleted (soft delete), or none;
 *                                   1 to 100 ids, an id listed twice counts once
 *                                 | 404 model_not_found (a listed model is unknown or deleted)
 *                                 | 409 model_not_deletable (a listed model is queued or in
 *                                   training, or deploying, deployed or stopping)
 *                                 | 422 validation body
 *   There is no DELETE /models/{id}. A deleted model's detail answers 404 training_job_not_found.
 * Download (backend change `download-models`): one .zip of several models, built by a worker job
 * in the object storage; no file bytes come from the API.
 *   POST /model-downloads { ids }  → 202 ModelArchiveJob (`pending`); at least 1 id, no upper
 *                                    limit, an id listed twice counts once; all or none
 *                                  | 404 model_not_found | 409 model_not_downloadable (a listed
 *                                    model's training is not completed) | 409 model_file_missing
 *                                    (a refusal about models names them in `modelIds`)
 *                                  | 413 download_too_large | 429 too_many_downloads
 *                                  | 503 storage_unavailable | 422 validation body
 *   GET  /model-downloads/{id}     → ModelArchiveJob | 404 download_not_found
 *   The file name is the backend's: "{modelName}.zip" for one model, "models-YYYYMMDD-HHMMSS.zip"
 *   (request time, UTC) for several.
 */

/** Model types of the algorithm catalogue. Kept as plain strings: an unknown one shows its code. */
export const MODEL_TYPES = ['anomaly_detection', 'fault_classification'] as const
export type ModelType = (typeof MODEL_TYPES)[number]

/** `queued`: stored and handed to the worker; `in_progress`: reported as running. */
export const TRAINING_STATUSES = ['queued', 'in_progress', 'completed', 'error'] as const
export type TrainingStatus = (typeof TRAINING_STATUSES)[number]

/**
 * `deploying` is shown as "In-progress". `stopped`: a Stop the inference service confirmed
 * (backend change `deploy-model-version`).
 */
export const DEPLOYMENT_STATUSES = [
  'not_deployed',
  'deploying',
  'deployed',
  'stopping',
  'stopped',
  'error',
] as const
export type DeploymentStatus = (typeof DEPLOYMENT_STATUSES)[number]

const countSchema = z.number().int().nonnegative()

export const modelSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  modelType: z.string().min(1),
  /** Name of the dataset when the training started; it does not follow a rename or a deletion. */
  datasetName: z.string(),
  /** Full name of the creator; `null` when the model has none. */
  createdBy: z.string().nullable(),
  createdAt: utcTimestampSchema,
  trainingStatus: z.string().min(1),
  /** Only for the training status `error`: the reason. */
  trainingErrorMessage: z.string().nullable(),
  deploymentStatus: z.string().min(1),
  /** Only for the deployment status `error`: the reason. */
  deploymentErrorMessage: z.string().nullable(),
})
export type Model = z.infer<typeof modelSchema>

export const MODEL_SORT_FIELDS = [
  'name',
  'modelType',
  'datasetName',
  'createdBy',
  'createdAt',
  'trainingStatus',
  'deploymentStatus',
] as const
export const modelSortFieldSchema = z.enum(MODEL_SORT_FIELDS)
export type ModelSortField = z.infer<typeof modelSortFieldSchema>

export const modelListSchema = z.object({
  items: z.array(modelSchema),
  /** Models matching the search and the filter, not only those on this page. */
  total: countSchema,
})
export type ModelList = z.infer<typeof modelListSchema>

export const modelSummarySchema = z.object({
  total: countSchema,
  /** Deployment status `deployed`. */
  running: countSchema,
  /** Training status `completed`. */
  completedTraining: countSchema,
  /** Training status `error` or deployment status `error`, each model counted once. */
  issues: countSchema,
})
export type ModelSummary = z.infer<typeof modelSummarySchema>

/** At most this many models are deleted with one request. */
export const MODEL_DELETE_MAX_IDS = 100

export const modelDeleteRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(MODEL_DELETE_MAX_IDS),
})
export type ModelDeleteRequest = z.infer<typeof modelDeleteRequestSchema>

export const MODEL_ERROR_CODES = {
  /** A listed model is unknown or was deleted meanwhile. */
  notFound: 'model_not_found',
  /** A listed model is in training or deployed (it changed after the list was read). */
  notDeletable: 'model_not_deletable',
  /** A listed model's training is not completed (it changed after the list was read). */
  notDownloadable: 'model_not_downloadable',
  /** Unknown archive job, a removed one, or a job of another user. */
  downloadNotFound: 'download_not_found',
} as const

export const modelArchiveRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1),
})
export type ModelArchiveRequest = z.infer<typeof modelArchiveRequestSchema>

/** `pending`: waiting or being built. `expired`: the archive is no longer kept. */
export const MODEL_ARCHIVE_STATUSES = ['pending', 'completed', 'error', 'expired'] as const
export const modelArchiveStatusSchema = z.enum(MODEL_ARCHIVE_STATUSES)
export type ModelArchiveStatus = z.infer<typeof modelArchiveStatusSchema>

export const modelArchiveJobSchema = z
  .object({
    id: z.string().min(1),
    status: modelArchiveStatusSchema,
    modelCount: countSchema,
    /** Sum of the sizes of the models' files. */
    totalBytes: countSchema,
    /** The name the browser saves the file under. */
    fileName: z.string().min(1),
    archiveBytes: countSchema.nullable(),
    /** Only for `completed`, where it is required. Works without the access token, for a while. */
    downloadUrl: z.string().min(1).nullable(),
    /** Only for `completed`: `downloadUrl` stops working at this time. */
    expiresAt: utcTimestampSchema.nullable(),
    /** Only for `completed`: the archive is kept until this time; then the job is `expired`. */
    availableUntil: utcTimestampSchema.nullable(),
    /** Only for `error`: the reason, an English sentence (not shown: the page has one message). */
    errorMessage: z.string().nullable(),
    createdAt: utcTimestampSchema,
    finishedAt: utcTimestampSchema.nullable(),
  })
  .refine((job) => job.status !== 'completed' || job.downloadUrl !== null, {
    message: 'A completed archive job carries its download address',
    path: ['downloadUrl'],
  })
export type ModelArchiveJob = z.infer<typeof modelArchiveJobSchema>
